import {
    FaCheckCircle,
    FaFileAlt,
    FaCreditCard,
    FaQrcode,
    FaArrowRight,
    FaBook,
} from 'react-icons/fa';
import { useWebsiteSettings } from '../../hooks/useWebsiteSettings';
import verifyPreviewImg from '../../assets/img/verify-preview.svg';
import JournalSlider from '../../component/public/JournalSlider';

const Home = () => {
    const { settings } = useWebsiteSettings();

    const services = [
        {
            icon: FaFileAlt,
            title: settings.systemTitle,
            text: 'Penerbitan dan pengelolaan surat penerimaan artikel (LOA) untuk jurnal universitas.',
        },
        {
            icon: FaBook,
            title: 'Manajemen jurnal',
            text: 'Pengaturan jurnal, issue, volume, dan metadata publikasi dalam satu sistem terpadu.',
        },
        {
            icon: FaCreditCard,
            title: 'Pembayaran publikasi',
            text: 'Invoice, tagihan, dan status pembayaran penulis terhubung langsung dengan data LOA.',
        },
    ];

    return (
        <>
            <section className="bg-canvas dark:bg-carbon">
                <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-20">
                    <div>
                        <p className="text-sm font-medium uppercase tracking-wider text-primary dark:text-primary-on-dark">
                            {settings.institutionName}
                        </p>
                        <h1 className="mt-3 text-3xl font-medium leading-tight text-ink dark:text-white sm:text-4xl lg:text-[2.5rem]">
                            {settings.systemTitle}
                        </h1>
                        <p className="mt-4 max-w-xl text-base leading-relaxed text-body dark:text-white/80">
                            Portal resmi {settings.systemTitle} {settings.institutionName} untuk
                            verifikasi keaslian LOA, manajemen publikasi jurnal, dan
                            layanan pembayaran artikel penulis.
                        </p>
                        <div className="mt-8">
                            <a href="#verifikasi" className="btn-primary inline-flex">
                                Cara verifikasi LOA
                                <FaArrowRight className="text-xs" />
                            </a>
                        </div>
                    </div>

                    <div className="card flex items-center justify-center p-8 lg:p-10">
                        <svg
                            viewBox="0 0 440 360"
                            className="w-full max-w-md"
                            aria-hidden
                        >
                            <rect
                                x="48"
                                y="40"
                                width="344"
                                height="280"
                                rx="12"
                                fill="currentColor"
                                className="text-canvas dark:text-tile-1"
                            />
                            <rect
                                x="68"
                                y="72"
                                width="304"
                                height="8"
                                rx="4"
                                fill="currentColor"
                                className="text-primary/30"
                            />
                            <rect
                                x="68"
                                y="92"
                                width="220"
                                height="6"
                                rx="3"
                                fill="currentColor"
                                className="text-primary/15"
                            />
                            <rect
                                x="68"
                                y="108"
                                width="180"
                                height="6"
                                rx="3"
                                fill="currentColor"
                                className="text-primary/15"
                            />
                            <rect
                                x="68"
                                y="140"
                                width="120"
                                height="28"
                                rx="6"
                                fill="currentColor"
                                className="text-primary"
                            />
                            <circle
                                cx="340"
                                cy="280"
                                r="36"
                                fill="currentColor"
                                className="text-accent-brand"
                            />
                            <path
                                d="M326 280l10 10 20-22"
                                stroke="#fff"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                            <rect
                                x="280"
                                y="200"
                                width="72"
                                height="72"
                                rx="8"
                                fill="currentColor"
                                className="text-ink/5 dark:text-white/10"
                            />
                            <rect x="292" y="212" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="308" y="212" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="324" y="212" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="292" y="228" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="308" y="228" width="12" height="12" rx="2" fill="currentColor" className="text-ink/40 dark:text-white/40" />
                            <rect x="324" y="228" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="292" y="244" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="308" y="244" width="12" height="12" rx="2" fill="currentColor" className="text-ink dark:text-white" />
                            <rect x="324" y="244" width="12" height="12" rx="2" fill="currentColor" className="text-ink/40 dark:text-white/40" />
                        </svg>
                    </div>
                </div>
            </section>

            <section
                id="jurnal"
                className="bg-surface-soft py-14 dark:bg-white/[0.03] sm:py-16"
            >
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <h2 className="text-xl font-medium text-ink dark:text-white sm:text-2xl">
                        Jurnal
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm text-muted">
                        Jurnal resmi {settings.institutionName} yang terintegrasi dengan sistem{' '}
                        {settings.systemTitle}.
                    </p>
                    <div className="mt-8">
                        <JournalSlider
                            institutionName={settings.institutionName}
                            systemTitle={settings.systemTitle}
                        />
                    </div>
                </div>
            </section>

            <section
                id="layanan"
                className="bg-canvas py-14 dark:bg-carbon sm:py-16"
            >
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <h2 className="text-xl font-medium text-ink dark:text-white sm:text-2xl">
                        Layanan sistem
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm text-muted">
                        Mendukung operasional jurnal dan layanan akademik{' '}
                        {settings.institutionName} di Tarakan, Kalimantan Utara.
                    </p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {services.map(({ icon: Icon, title, text }, index) => (
                            <article key={index} className="card p-6">
                                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xs bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-on-dark">
                                    <Icon className="h-4 w-4" />
                                </div>
                                <h3 className="text-base font-medium text-ink dark:text-white">
                                    {title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted">{text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="verifikasi" className="bg-surface-soft py-14 dark:bg-white/[0.03] sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
                        <div>
                            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xs bg-accent-brand/15 text-accent-brand">
                                <FaQrcode className="h-4 w-4" />
                            </div>
                            <h2 className="text-xl font-medium text-ink dark:text-white sm:text-2xl">
                                Verifikasi LOA online
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-body dark:text-white/80">
                                Setiap LOA resmi memiliki tautan verifikasi unik. Penulis,
                                reviewer, atau pihak ketiga dapat memindai kode QR pada
                                dokumen LOA atau membuka tautan verifikasi untuk melihat
                                detail artikel, jurnal, dan issue.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-muted">

                                <li className="flex items-start gap-2">
                                    <FaCheckCircle className="mt-0.5 shrink-0 text-primary dark:text-primary-on-dark" />
                                    Status terverifikasi ditandai secara jelas
                                </li>

                            </ul>
                        </div>
                        <div>
                            <div className="overflow-hidden rounded-md border border-hairline dark:border-white/10">
                                <img
                                    src={verifyPreviewImg}
                                    alt="Ilustrasi halaman verifikasi LOA"
                                    className="block w-full h-auto"
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Home;
